import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2, X, Check, Loader as Loader2, ChevronDown, ChevronUp, Globe, ShoppingCart } from 'lucide-react';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'EN' },
  { value: 'nl', label: 'NL' },
  { value: 'de', label: 'DE' },
];

const LANGUAGE_LABELS: Record<string, string> = { en: 'EN', nl: 'NL', de: 'DE' };

interface Reseller {
  id: string;
  name: string;
  discount_percent: number;
  margin_percent: number;
  default_language: string;
  domain: string | null;
  brand_color: string | null;
  email: string | null;
  address_street: string;
  address_city: string;
  address_postal_code: string;
  address_country: string;
  cart_url: string | null;
  cart_api_key_id: string | null;
  shop_url: string | null;
}

interface EditState {
  name: string;
  discount_percent: string;
  margin_percent: string;
  default_language: string;
  domain: string;
  brand_color: string;
  email: string;
  address_street: string;
  address_city: string;
  address_postal_code: string;
  address_country: string;
  cart_url: string;
  cart_api_key_id: string;
  shop_url: string;
}

const emptyEditState = (): EditState => ({
  name: '',
  discount_percent: '0',
  margin_percent: '0',
  default_language: 'en',
  domain: '',
  brand_color: '#212b4f',
  email: '',
  address_street: '',
  address_city: '',
  address_postal_code: '',
  address_country: '',
  cart_url: '',
  cart_api_key_id: '',
  shop_url: '',
});

interface ApiKeyOption {
  id: string;
  name: string;
  active: boolean;
}

function AddressFields({ state, onChange }: { state: EditState; onChange: (s: EditState) => void }) {
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Email</Label>
        <input
          type="email"
          value={state.email}
          onChange={e => onChange({ ...state, email: e.target.value })}
          placeholder="company@example.com"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Street</Label>
        <input
          type="text"
          value={state.address_street}
          onChange={e => onChange({ ...state, address_street: e.target.value })}
          placeholder="Street and number"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs text-slate-600">Postal code</Label>
          <input
            type="text"
            value={state.address_postal_code}
            onChange={e => onChange({ ...state, address_postal_code: e.target.value })}
            placeholder="Postal code"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-600">City</Label>
          <input
            type="text"
            value={state.address_city}
            onChange={e => onChange({ ...state, address_city: e.target.value })}
            placeholder="City"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Country</Label>
        <input
          type="text"
          value={state.address_country}
          onChange={e => onChange({ ...state, address_country: e.target.value })}
          placeholder="Country"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
        />
      </div>
    </div>
  );
}

function CartFields({ state, onChange, apiKeys }: { state: EditState; onChange: (s: EditState) => void; apiKeys: ApiKeyOption[] }) {
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Shop URL</Label>
        <input
          type="url"
          value={state.shop_url}
          onChange={e => onChange({ ...state, shop_url: e.target.value })}
          placeholder="https://yourshop.com/product/shelving"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] font-mono"
        />
        <p className="text-xs text-slate-400">The product page in the shop. When a customer clicks "Add to Cart", the configurator saves the config and opens this URL with <code className="bg-slate-100 px-1 rounded">?add_config=1&configurator=shelving&config_id=&#123;uuid&#125;</code> appended.</p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Cart URL <span className="font-normal text-slate-400">(legacy)</span></Label>
        <input
          type="url"
          value={state.cart_url}
          onChange={e => onChange({ ...state, cart_url: e.target.value })}
          placeholder="https://yourshop.com/?add-to-cart=123&config_id={config_id}"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] font-mono"
        />
        <p className="text-xs text-slate-400">Legacy template URL. Use <code className="bg-slate-100 px-1 rounded">{'{config_id}'}</code> as placeholder. Prefer Shop URL above for new setups.</p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Cart API Key</Label>
        <select
          value={state.cart_api_key_id}
          onChange={e => onChange({ ...state, cart_api_key_id: e.target.value })}
          className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] bg-white"
        >
          <option value="">— None —</option>
          {apiKeys.map(k => (
            <option key={k.id} value={k.id} disabled={!k.active}>
              {k.name}{!k.active ? ' (revoked)' : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-400">The API key WooCommerce uses as Bearer token when calling the configuration endpoint. Manage keys in the API Keys tab.</p>
      </div>
    </div>
  );
}

function formatAddress(r: Reseller): string {
  const parts = [
    r.address_street,
    [r.address_postal_code, r.address_city].filter(Boolean).join(' '),
    r.address_country,
  ].filter(Boolean);
  return parts.join(', ');
}

export function ResellersTab() {
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>(emptyEditState());
  const [showNew, setShowNew] = useState(false);
  const [newState, setNewState] = useState<EditState>(emptyEditState());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKeyOption[]>([]);

  const fetchApiKeys = async () => {
    const { data } = await supabase
      .from('api_keys')
      .select('id, name, active')
      .order('name');
    if (data) setApiKeys(data);
  };

  const fetchResellers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('resellers')
      .select('id, name, discount_percent, margin_percent, default_language, domain, brand_color, email, address_street, address_city, address_postal_code, address_country, cart_url, cart_api_key_id, shop_url')
      .order('name');
    if (!error && data) setResellers(data);
    setLoading(false);
  };

  useEffect(() => { fetchResellers(); fetchApiKeys(); }, []);

  const startEdit = (r: Reseller) => {
    setEditingId(r.id);
    setExpandedId(r.id);
    setEditState({
      name: r.name,
      discount_percent: String(r.discount_percent),
      margin_percent: String(r.margin_percent),
      default_language: r.default_language ?? 'en',
      domain: r.domain ?? '',
      brand_color: r.brand_color ?? '#212b4f',
      email: r.email ?? '',
      address_street: r.address_street ?? '',
      address_city: r.address_city ?? '',
      address_postal_code: r.address_postal_code ?? '',
      address_country: r.address_country ?? '',
      cart_url: r.cart_url ?? '',
      cart_api_key_id: r.cart_api_key_id ?? '',
      shop_url: r.shop_url ?? '',
    });
    setError(null);
  };

  const cancelEdit = () => { setEditingId(null); setError(null); };

  const saveEdit = async (id: string) => {
    setSaving(true);
    setError(null);
    const { error } = await supabase
      .from('resellers')
      .update({
        name: editState.name,
        discount_percent: parseFloat(editState.discount_percent) || 0,
        margin_percent: parseFloat(editState.margin_percent) || 0,
        default_language: editState.default_language,
        domain: editState.domain.trim() || null,
        brand_color: editState.brand_color.trim() || null,
        email: editState.email.trim() || null,
        address_street: editState.address_street,
        address_city: editState.address_city,
        address_postal_code: editState.address_postal_code,
        address_country: editState.address_country,
        cart_url: editState.cart_url.trim() || null,
        cart_api_key_id: editState.cart_api_key_id || null,
        shop_url: editState.shop_url.trim() || null,
      })
      .eq('id', id);
    setSaving(false);
    if (error) { setError(error.message); return; }
    setEditingId(null);
    fetchResellers();
  };

  const deleteReseller = async (id: string) => {
    if (!confirm('Delete this reseller? Associated users will lose their reseller link.')) return;
    await supabase.from('resellers').delete().eq('id', id);
    fetchResellers();
  };

  const createReseller = async () => {
    setSaving(true);
    setError(null);
    const { error } = await supabase.from('resellers').insert({
      name: newState.name,
      discount_percent: parseFloat(newState.discount_percent) || 0,
      margin_percent: parseFloat(newState.margin_percent) || 0,
      default_language: newState.default_language,
      domain: newState.domain.trim() || null,
      brand_color: newState.brand_color.trim() || null,
      email: newState.email.trim() || null,
      address_street: newState.address_street,
      address_city: newState.address_city,
      address_postal_code: newState.address_postal_code,
      address_country: newState.address_country,
      cart_url: newState.cart_url.trim() || null,
      cart_api_key_id: newState.cart_api_key_id || null,
      shop_url: newState.shop_url.trim() || null,
    });
    setSaving(false);
    if (error) { setError(error.message); return; }
    setShowNew(false);
    setNewState(emptyEditState());
    fetchResellers();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Resellers</h2>
        <Button
          onClick={() => { setShowNew(true); setError(null); }}
          className="bg-[#212b4f] hover:bg-[#2d3a6b] text-white h-8 text-sm gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          New Reseller
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {showNew && (
          <div className="border-b border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">New Reseller</p>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Name</Label>
                <input
                  type="text"
                  value={newState.name}
                  onChange={e => setNewState(s => ({ ...s, name: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Discount %</Label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newState.discount_percent}
                  onChange={e => setNewState(s => ({ ...s, discount_percent: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Margin %</Label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newState.margin_percent}
                  onChange={e => setNewState(s => ({ ...s, margin_percent: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Language</Label>
                <select
                  value={newState.default_language}
                  onChange={e => setNewState(s => ({ ...s, default_language: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] bg-white"
                >
                  {LANGUAGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Domain</Label>
                <input
                  type="text"
                  value={newState.domain}
                  onChange={e => setNewState(s => ({ ...s, domain: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                  placeholder="reseller.yourdomain.com"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Brand Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newState.brand_color || '#212b4f'}
                    onChange={e => setNewState(s => ({ ...s, brand_color: e.target.value }))}
                    className="h-8 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
                  />
                  <input
                    type="text"
                    value={newState.brand_color}
                    onChange={e => setNewState(s => ({ ...s, brand_color: e.target.value }))}
                    className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] font-mono"
                    placeholder="#212b4f"
                  />
                </div>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Company Address</p>
              <div className="max-w-lg">
                <AddressFields state={newState} onChange={setNewState} />
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <ShoppingCart className="h-3.5 w-3.5" />
                WooCommerce Cart
              </p>
              <CartFields state={newState} onChange={setNewState} apiKeys={apiKeys} />
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={createReseller}
                disabled={saving || !newState.name.trim()}
                className="bg-[#212b4f] hover:bg-[#2d3a6b] text-white h-8 text-sm gap-1.5"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Create
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setShowNew(false); setError(null); }}
                className="h-8 text-sm text-slate-600"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : resellers.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">No resellers yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Domain</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Discount %</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Margin %</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Language</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {resellers.map(r => (
                <React.Fragment key={r.id}>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      <div className="flex items-center gap-1.5">
                        {r.name}
                        {formatAddress(r) && (
                          <button
                            onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                            title="Toggle address"
                          >
                            {expandedId === r.id
                              ? <ChevronUp className="h-3.5 w-3.5" />
                              : <ChevronDown className="h-3.5 w-3.5" />
                            }
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.domain ? (
                          <span className="inline-flex items-center gap-1 text-slate-600 text-xs">
                            <Globe className="h-3 w-3 text-slate-400" />
                            {r.domain}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs italic">—</span>
                        )}
                        {r.brand_color && (
                          <span
                            title={r.brand_color}
                            className="inline-block h-3.5 w-3.5 rounded-full border border-slate-200 shrink-0"
                            style={{ backgroundColor: r.brand_color }}
                          />
                        )}
                        {r.shop_url && (
                          <span
                            className="inline-flex items-center gap-1 text-blue-700 text-xs bg-blue-50 px-1.5 py-0.5 rounded"
                            title={r.shop_url}
                          >
                            <ShoppingCart className="h-3 w-3" />
                            Shop
                          </span>
                        )}
                        {r.cart_url && (
                          <span
                            className="inline-flex items-center gap-1 text-green-700 text-xs bg-green-50 px-1.5 py-0.5 rounded"
                            title={`${r.cart_url}${r.cart_api_key_id ? ` · Key: ${apiKeys.find(k => k.id === r.cart_api_key_id)?.name ?? r.cart_api_key_id}` : ''}`}
                          >
                            <ShoppingCart className="h-3 w-3" />
                            Cart{r.cart_api_key_id ? ` · ${apiKeys.find(k => k.id === r.cart_api_key_id)?.name ?? ''}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.discount_percent}%</td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.margin_percent}%</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-600">
                        {LANGUAGE_LABELS[r.default_language] ?? r.default_language.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => startEdit(r)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-[#212b4f] hover:bg-slate-100 transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteReseller(r.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {expandedId === r.id && editingId !== r.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 py-2 text-xs text-slate-500">
                        {formatAddress(r) || <span className="italic">No address on file</span>}
                      </td>
                    </tr>
                  )}

                  {editingId === r.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Name</Label>
                            <input
                              type="text"
                              value={editState.name}
                              onChange={e => setEditState(s => ({ ...s, name: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Discount %</Label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={editState.discount_percent}
                              onChange={e => setEditState(s => ({ ...s, discount_percent: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Margin %</Label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={editState.margin_percent}
                              onChange={e => setEditState(s => ({ ...s, margin_percent: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Language</Label>
                            <select
                              value={editState.default_language}
                              onChange={e => setEditState(s => ({ ...s, default_language: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] bg-white"
                            >
                              {LANGUAGE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Domain</Label>
                            <input
                              type="text"
                              value={editState.domain}
                              onChange={e => setEditState(s => ({ ...s, domain: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                              placeholder="reseller.yourdomain.com"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Brand Color</Label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={editState.brand_color || '#212b4f'}
                                onChange={e => setEditState(s => ({ ...s, brand_color: e.target.value }))}
                                className="h-8 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
                              />
                              <input
                                type="text"
                                value={editState.brand_color}
                                onChange={e => setEditState(s => ({ ...s, brand_color: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] font-mono"
                                placeholder="#212b4f"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-slate-200 pt-3 mb-3">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Company Address</p>
                          <div className="max-w-lg">
                            <AddressFields state={editState} onChange={setEditState} />
                          </div>
                        </div>
                        <div className="border-t border-slate-200 pt-3 mb-3">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <ShoppingCart className="h-3.5 w-3.5" />
                            WooCommerce Cart
                          </p>
                          <CartFields state={editState} onChange={setEditState} apiKeys={apiKeys} />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveEdit(r.id)}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-[#212b4f] text-white hover:bg-[#2d3a6b] transition-colors disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-200 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
